<template>
    <div class="h-screen text-white bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 overflow-hidden">
        <!-- Header -->
        <header class="px-8 py-3 border-b bg-white/10 backdrop-blur-sm border-white/20">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4">
                    <div
                        class="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r from-yellow-400 to-rose-900">
                        <ApplicationLogo class="w-10 h-10 text-dark" />
                    </div>
                    <div>
                        <h1 class="text-2xl font-bold">FEB INFORMASI</h1>
                        <p class="text-sm text-blue-200">Sistem Informasi Digital Fakultas</p>
                    </div>
                </div>

                <div class="text-right">
                    <div class="font-mono text-3xl font-bold">{{ currentTime }}</div>
                    <div class="text-blue-200">{{ currentDate }}</div>
                    <div class="flex items-center justify-end mt-2 text-xs"
                        :class="refreshCountdown <= 30 ? 'refresh-warning' : 'text-blue-300'">
                        <i class="mr-1 fas fa-sync-alt" :class="{ 'animate-spin': refreshCountdown <= 10 }"></i>
                        Auto-refresh: {{ formatCountdown }}
                    </div>
                </div>
            </div>
        </header>

        <!-- Main Content -->
        <div class="flex h-[calc(100vh-80px)]">
            <!-- Left Side - Kehadiran Info -->
            <div class="w-1/2 p-6">
                <div class="h-full p-6 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/20">
                    <div class="flex items-center mb-6">
                        <div class="flex items-center justify-center w-8 h-8 mr-3 bg-green-500 rounded-full">
                            <i class="text-white fas fa-user-check"></i>
                        </div>
                        <h2 class="text-2xl font-semibold">Kehadiran Hari Ini</h2>
                    </div>

                    <!-- Kehadiran List -->
                    <div class="relative p-4 overflow-hidden bg-white/5 rounded-xl">
                        <!-- Carousel Container -->
                        <div class="relative">
                            <!-- Header dengan jumlah total kehadiran -->
                            <div class="flex items-center justify-between mb-4 kehadiran-header">
                                <div class="text-sm text-blue-200">
                                    Total: {{ sortedKehadiran.length }} orang
                                </div>
                                <div class="text-xs text-gray-400">
                                    {{ currentDate }}
                                </div>
                            </div>

                            <div class="overflow-hidden max-h-[calc(100vh-280px)]">
                                <div v-if="!sortedKehadiran || sortedKehadiran.length === 0"
                                    class="py-8 text-center text-gray-400">
                                    <i class="mb-2 text-4xl fas fa-users-slash"></i>
                                    <p>Tidak ada data kehadiran</p>
                                </div>
                                <div v-else class="relative kehadiran-container">
                                    <!-- Carousel Progress Indicator -->
                                    <div v-if="kehadiranTotalPages > 1" class="flex items-center justify-between mb-4">
                                        <div class="text-xs text-blue-200">
                                            {{ currentKehadiranIndex + 1 }} dari {{ kehadiranTotalPages }} halaman
                                        </div>
                                        <div class="flex space-x-2">
                                            <div v-for="page in kehadiranTotalPages" :key="page"
                                                class="w-2 h-2 transition-all duration-500 rounded-full"
                                                :class="page - 1 === currentKehadiranIndex ? 'bg-cyan-400 w-6 shadow-lg shadow-cyan-400/50' : 'bg-white/30'">
                                            </div>
                                        </div>
                                        <div class="text-xs text-gray-400">
                                            Auto {{ 6 }}s
                                        </div>
                                    </div>

                                    <div class="space-y-3 min-h-[calc(100vh-350px)] relative">
                                        <transition-group name="kehadiran-slide-up" tag="div" class="space-y-3">
                                            <div v-for="(kehadiran, index) in currentKehadiranPage"
                                                :key="kehadiran?.id || `kehadiran-${currentKehadiranIndex}-${index}`"
                                                class="flex items-center justify-between px-2 py-3 transition-all duration-300 border-b rounded-lg border-white/10 last:border-b-0 hover:bg-white/5 kehadiran-item"
                                                :style="{
                                                    animationDelay: (index * 80 + 200) + 'ms'
                                                }">
                                                <div class="flex items-center space-x-3">
                                                    <div
                                                        class="relative flex items-center justify-center w-12 h-12 transition-transform duration-200 transform rounded-full shadow-lg bg-gradient-to-r from-cyan-400 to-blue-500 hover:scale-105">
                                                        <img v-if="kehadiran.foto && !imageErrors[kehadiran.id]"
                                                            :src="getPhotoUrl(kehadiran.foto)"
                                                            :alt="kehadiran.nama_pimpinan || 'N/A'"
                                                            class="object-cover w-full h-full rounded-full"
                                                            @error="handleImageError(kehadiran.id)"
                                                            @load="handleImageLoad(kehadiran.id)">
                                                        <span v-else class="text-sm font-semibold">
                                                            {{ (kehadiran.nama_pimpinan || 'N/A').charAt(0) }}
                                                        </span>
                                                        <!-- Status indicator with pulse animation -->
                                                        <div class="absolute w-4 h-4 border-2 border-white rounded-full -bottom-1 -right-1 pulse-status"
                                                            :class="getStatusIndicatorClass(kehadiran?.status)"></div>
                                                    </div>
                                                    <div class="flex-1">
                                                        <div class="font-medium text-white">{{ kehadiran.nama_pimpinan
                                                            || 'N/A' }}</div>
                                                        <div class="text-xs text-gray-300">{{ kehadiran.jabatan || 'N/A'
                                                            }}</div>
                                                    </div>
                                                </div>
                                                <div class="text-right">
                                                    <div class="px-2 py-1 text-sm font-semibold rounded-full"
                                                        :class="getStatusBadgeClass(kehadiran?.status)">
                                                        {{ getStatusLabel(kehadiran?.status) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </transition-group>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Side - Agenda -->
            <div class="w-1/2 p-6">
                <div class="h-full">
                    <!-- Agenda Section -->
                    <div v-if="agenda_mendatang && agenda_mendatang.length > 0"
                        class="h-full p-6 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/20">
                        <div class="flex items-center mb-6">
                            <div class="flex items-center justify-center w-8 h-8 mr-3 bg-purple-500 rounded-full">
                                <i class="text-white fas fa-calendar-alt"></i>
                            </div>
                            <h2 class="text-2xl font-semibold">Agenda Mendatang</h2>
                        </div>

                        <div class="space-y-4 overflow-y-auto max-h-[calc(100vh-280px)]">
                            <transition-group name="agenda-slide" tag="div" class="space-y-4">
                                <div v-for="agenda in currentAgendaPage" :key="agenda.id"
                                    class="p-4 transition-all duration-300 border-l-4 border-purple-500 bg-white/5 rounded-xl hover:bg-white/10">
                                    <h3 class="text-lg font-semibold text-purple-200">{{ agenda.judul }}</h3>
                                    <div class="flex items-center mt-2 text-sm text-gray-300">
                                        <i class="mr-2 fas fa-map-marker-alt"></i>
                                        <span>{{ agenda.tempat }}</span>
                                    </div>
                                    <div class="flex items-center mt-1 text-sm text-gray-400">
                                        <i class="mr-2 fas fa-clock"></i>
                                        <span>{{ formatDate(agenda.tanggal) }} • {{ agenda.waktu_mulai }}</span>
                                    </div>
                                </div>
                            </transition-group>
                        </div>
                    </div>

                    <!-- Empty state untuk agenda -->
                    <div v-else
                        class="flex items-center justify-center h-full p-6 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/20">
                        <div class="text-center text-gray-400">
                            <i class="mb-4 text-6xl fas fa-calendar-times"></i>
                            <h3 class="mb-2 text-xl font-semibold">Tidak Ada Agenda</h3>
                            <p>Belum ada agenda yang dijadwalkan</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bottom Ticker - Pengumuman -->
        <div class="fixed bottom-0 left-0 right-0 py-3 overflow-hidden bg-gradient-to-r from-cyan-600 to-blue-600">
            <div class="px-4 text-lg font-medium scroll-text whitespace-nowrap">
                <span v-if="runningTextContent">{{ runningTextContent }}</span>
                <span v-else>📢 Selamat datang di Sistem Informasi Digital Fakultas Ekonomi dan Bisnis</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { router } from '@inertiajs/vue3';
import ApplicationLogo from '@/Components/ApplicationLogo.vue';

const props = defineProps([
    'kehadiran_hari_ini',
    'agenda_hari_ini',
    'agenda_mendatang',
    'pengumuman_aktif',
    'pengumuman_urgent',
    'undangan_hari_ini',
    'undangan_mendatang',
    'stats'
]);

// Reactive refs
const currentTimeRef = ref(new Date());
const imageErrors = ref({});
const imageLoaded = ref({});
const currentKehadiranIndex = ref(0);
const currentAgendaIndex = ref(0);
const refreshCountdown = ref(300); // 5 minutes = 300 seconds

// Intervals
let timeInterval = null;
let kehadiranInterval = null;
let countdownInterval = null;

// Computed properties
const currentTime = computed(() => {
    return currentTimeRef.value.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
});

const currentDate = computed(() => {
    return currentTimeRef.value.toLocaleDateString('id-ID', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
});

const sortedKehadiran = computed(() => {
    const data = props.kehadiran_hari_ini || [];
    if (!Array.isArray(data)) return [];

    return data.sort((a, b) => {
        const statusOrder = { hadir: 1, terlambat: 2, izin: 3, sakit: 4, tidak_hadir: 5 };
        const statusA = statusOrder[a?.status] || 99;
        const statusB = statusOrder[b?.status] || 99;

        if (statusA !== statusB) {
            return statusA - statusB;
        }

        const nameA = (a?.nama_pimpinan || 'Z').toUpperCase();
        const nameB = (b?.nama_pimpinan || 'Z').toUpperCase();
        return nameA.localeCompare(nameB);
    });
});

const runningTextContent = computed(() => {
    const urgentPengumuman = (props.pengumuman_urgent || []).map((p) => ({
        ...p,
        priority: "urgent",
    }));
    const aktivePengumuman = (props.pengumuman_aktif || []).map((p) => ({
        ...p,
        priority: "active",
    }));

    const allPengumumanData = [...urgentPengumuman, ...aktivePengumuman];

    if (allPengumumanData.length === 0) return null;

    const textItems = allPengumumanData.map((p) => {
        const icon = p.priority === "urgent" ? "🚨" : "📢";
        const date = p.tanggal_publikasi ? formatDate(p.tanggal_publikasi) : "";
        return `${icon} ${p.judul} - ${p.penerbit} ${date ? `(${date})` : ""}`;
    });

    return textItems.join(" • ") + " • " + textItems.join(" • ") + " • ";
});

// Pagination computeds
const currentKehadiranPage = computed(() => {
    const itemsPerPage = 6;
    const startIndex = currentKehadiranIndex.value * itemsPerPage;
    return sortedKehadiran.value.slice(startIndex, startIndex + itemsPerPage);
});

const kehadiranTotalPages = computed(() => {
    const itemsPerPage = 6;
    return Math.ceil(sortedKehadiran.value.length / itemsPerPage);
});

const currentAgendaPage = computed(() => {
    const itemsPerPage = 5;
    const startIndex = currentAgendaIndex.value * itemsPerPage;
    return (props.agenda_mendatang || []).slice(startIndex, startIndex + itemsPerPage);
});

// Format countdown for display
const formatCountdown = computed(() => {
    const minutes = Math.floor(refreshCountdown.value / 60);
    const seconds = refreshCountdown.value % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

// Helper functions
const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const getStatusLabel = (status) => {
    if (!status) return "N/A";
    const labels = {
        hadir: "Hadir",
        tidak_hadir: "Tidak Hadir",
        terlambat: "Terlambat",
        sakit: "Sakit",
        izin: "Izin",
    };
    return labels[status] || status;
};

const getStatusIndicatorClass = (status) => {
    const classes = {
        hadir: "bg-green-500",
        tidak_hadir: "bg-red-500",
        terlambat: "bg-yellow-500",
        sakit: "bg-blue-500",
        izin: "bg-purple-500",
    };
    return classes[status] || "bg-gray-500";
};

const getStatusBadgeClass = (status) => {
    const classes = {
        hadir: "bg-green-500/20 text-green-300 border border-green-500/30",
        tidak_hadir: "bg-red-500/20 text-red-300 border border-red-500/30",
        terlambat: "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30",
        sakit: "bg-blue-500/20 text-blue-300 border border-blue-500/30",
        izin: "bg-purple-500/20 text-purple-300 border border-purple-500/30",
    };
    return classes[status] || "bg-gray-500/20 text-gray-300 border border-gray-500/30";
};

const getPhotoUrl = (foto) => {
    if (!foto) return '';
    if (foto.startsWith('http')) return foto;
    return `/storage/${foto}`;
};

const handleImageError = (id) => {
    imageErrors.value[id] = true;
};

const handleImageLoad = (id) => {
    imageLoaded.value[id] = true;
    imageErrors.value[id] = false;
};

// Clock update function
const updateTime = () => {
    currentTimeRef.value = new Date();
};

// Rotation functions
const rotateKehadiran = () => {
    if (kehadiranTotalPages.value > 1) {
        currentKehadiranIndex.value = (currentKehadiranIndex.value + 1) % kehadiranTotalPages.value;
    }
};

const rotateAgenda = () => {
    const itemsPerPage = 5;
    const totalPages = Math.ceil((props.agenda_mendatang || []).length / itemsPerPage);
    if (totalPages > 1) {
        currentAgendaIndex.value = (currentAgendaIndex.value + 1) % totalPages;
    }
};

// Auto-refresh function
const autoRefreshPage = () => {
    router.reload({
        only: ['kehadiran_hari_ini', 'agenda_hari_ini', 'agenda_mendatang', 'pengumuman_aktif', 'pengumuman_urgent', 'undangan_hari_ini', 'undangan_mendatang', 'stats'],
        preserveScroll: true,
        preserveState: true
    });
    refreshCountdown.value = 300;
};

// Countdown function
const updateCountdown = () => {
    if (refreshCountdown.value > 0) {
        refreshCountdown.value--;
    } else {
        autoRefreshPage();
    }
};

// Watch for kehadiran data changes
watch(() => sortedKehadiran.value.length, (newLength) => {
    if (kehadiranInterval) {
        clearInterval(kehadiranInterval);
        kehadiranInterval = null;
    }

    currentKehadiranIndex.value = 0;

    if (newLength > 6) {
        kehadiranInterval = setInterval(rotateKehadiran, 6000);
    }
});

// Lifecycle hooks
onMounted(() => {
    timeInterval = setInterval(updateTime, 1000);
    countdownInterval = setInterval(updateCountdown, 1000);

    if (sortedKehadiran.value.length > 6) {
        kehadiranInterval = setInterval(rotateKehadiran, 6000);
    }
});

onUnmounted(() => {
    if (timeInterval) clearInterval(timeInterval);
    if (kehadiranInterval) clearInterval(kehadiranInterval);
    if (countdownInterval) clearInterval(countdownInterval);
});
</script>

<style scoped>
/* Animations */
@keyframes scroll {
    0% {
        transform: translateX(100%);
    }

    100% {
        transform: translateX(-100%);
    }
}

.scroll-text {
    animation: scroll 30s linear infinite;
}

/* Carousel animations */
.kehadiran-slide-up-enter-active {
    transition: all 0.8s ease-out;
    transform: translateY(0);
    opacity: 1;
}

.kehadiran-slide-up-enter-from {
    transform: translateY(50px);
    opacity: 0;
}

.kehadiran-slide-up-leave-active {
    transition: all 0.5s ease-in;
}

.kehadiran-slide-up-leave-to {
    transform: translateY(-30px);
    opacity: 0;
}

.agenda-slide-enter-active,
.agenda-slide-leave-active {
    transition: all 0.5s ease;
}

.agenda-slide-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.agenda-slide-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

/* Pulse animation for status indicators */
.pulse-status {
    animation: pulse-ring 2s infinite;
}

@keyframes pulse-ring {
    0% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.1);
        opacity: 0.8;
    }

    100% {
        transform: scale(1);
    }
}

/* Refresh warning animation */
.refresh-warning {
    color: #fed7aa;
    animation: pulse 1s infinite;
}

/* Kehadiran item animation */
.kehadiran-item {
    animation: slideInUp 0.6s ease-out forwards;
    opacity: 0;
    transform: translateY(20px);
}

@keyframes slideInUp {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>